import React, { useState, useRef, useEffect } from 'react';
import { Volume2, Play, Pause } from 'lucide-react';
import { SeoMeta } from '../components/seo/SeoMeta';

interface PinyinExample {
  character: string;
  pinyin: string;
  meaning: string;
  audioUrl: string;
}

interface ToneGuide {
  tone: string;
  description: string;
  examples: PinyinExample[];
}

export function PronunciationGuide() {
  const [playing, setPlaying] = useState<string | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const toneGuides: ToneGuide[] = [
    {
      tone: "First Tone (ā)",
      description: "High and level, like singing a high note",
      examples: [
        { character: "妈", pinyin: "mā", meaning: "mother", audioUrl: "ma1" },
        { character: "高", pinyin: "gāo", meaning: "high", audioUrl: "gao1" },
        { character: "天", pinyin: "tiān", meaning: "sky, heaven", audioUrl: "tian1" },
        { character: "山", pinyin: "shān", meaning: "mountain", audioUrl: "shan1" },
        { character: "花", pinyin: "huā", meaning: "flower", audioUrl: "hua1" },
        { character: "星", pinyin: "xīng", meaning: "star", audioUrl: "xing1" }
      ]
    },
    {
      tone: "Second Tone (á)",
      description: "Rising tone, like asking a question",
      examples: [
        { character: "国", pinyin: "guó", meaning: "country", audioUrl: "guo2" },
        { character: "明", pinyin: "míng", meaning: "bright", audioUrl: "ming2" },
        { character: "文", pinyin: "wén", meaning: "literature, culture", audioUrl: "wen2" },
        { character: "华", pinyin: "huá", meaning: "magnificent, China", audioUrl: "hua2" },
        { character: "阳", pinyin: "yáng", meaning: "sun, positive", audioUrl: "yang2" },
        { character: "强", pinyin: "qiáng", meaning: "strong, powerful", audioUrl: "qiang2" }
      ]
    },
    {
      tone: "Third Tone (ǎ)",
      description: "Falling then rising, like a dip",
      examples: [
        { character: "美", pinyin: "měi", meaning: "beautiful", audioUrl: "mei3" },
        { character: "好", pinyin: "hǎo", meaning: "good", audioUrl: "hao3" },
        { character: "永", pinyin: "yǒng", meaning: "forever, eternal", audioUrl: "yong3" },
        { character: "勇", pinyin: "yǒng", meaning: "brave, courageous", audioUrl: "yong3b" },
        { character: "水", pinyin: "shuǐ", meaning: "water", audioUrl: "shui3" },
        { character: "海", pinyin: "hǎi", meaning: "ocean, sea", audioUrl: "hai3" }
      ]
    },
    {
      tone: "Fourth Tone (à)",
      description: "Sharp falling tone, like a command",
      examples: [
        { character: "大", pinyin: "dà", meaning: "big", audioUrl: "da4" },
        { character: "爱", pinyin: "ài", meaning: "love", audioUrl: "ai4" },
        { character: "智", pinyin: "zhì", meaning: "wisdom, intelligence", audioUrl: "zhi4" },
        { character: "德", pinyin: "dé", meaning: "virtue, morality", audioUrl: "de4" },
        { character: "静", pinyin: "jìng", meaning: "quiet, calm", audioUrl: "jing4" },
        { character: "亮", pinyin: "liàng", meaning: "bright, shining", audioUrl: "liang4" }
      ]
    },
    {
      tone: "Neutral Tone",
      description: "Light and short, like a whisper",
      examples: [
        { character: "的", pinyin: "de", meaning: "possessive particle", audioUrl: "de0" },
        { character: "吗", pinyin: "ma", meaning: "question particle", audioUrl: "ma0" },
        { character: "子", pinyin: "zi", meaning: "child, son", audioUrl: "zi0" },
        { character: "儿", pinyin: "er", meaning: "child, son", audioUrl: "er0" }
      ]
    }
  ];

  // Common Chinese names for pronunciation practice
  const commonNames = [
    { fullName: "李明", pinyin: "Lǐ Míng", meaning: "Bright Lee", audioUrl: "liming" },
    { fullName: "王强", pinyin: "Wáng Qiáng", meaning: "Strong Wang", audioUrl: "wangqiang" },
    { fullName: "张美", pinyin: "Zhāng Měi", meaning: "Beautiful Zhang", audioUrl: "zhangmei" },
    { fullName: "刘文", pinyin: "Liú Wén", meaning: "Cultured Liu", audioUrl: "liuwen" },
    { fullName: "陈静", pinyin: "Chén Jìng", meaning: "Calm Chen", audioUrl: "chenjing" },
    { fullName: "杨华", pinyin: "Yáng Huá", meaning: "Magnificent Yang", audioUrl: "yanghua" },
    { fullName: "黄勇", pinyin: "Huáng Yǒng", meaning: "Brave Huang", audioUrl: "huangyong" },
    { fullName: "赵亮", pinyin: "Zhào Liàng", meaning: "Bright Zhao", audioUrl: "zhaoliang" }
  ];

  // Common surnames
  const commonSurnames = [
    { surname: "李", pinyin: "Lǐ", meaning: "Plum tree", audioUrl: "li" },
    { surname: "王", pinyin: "Wáng", meaning: "King", audioUrl: "wang" },
    { surname: "张", pinyin: "Zhāng", meaning: "Stretch", audioUrl: "zhang" },
    { surname: "刘", pinyin: "Liú", meaning: "Kill (ancient)", audioUrl: "liu" },
    { surname: "陈", pinyin: "Chén", meaning: "Exhibit", audioUrl: "chen" },
    { surname: "杨", pinyin: "Yáng", meaning: "Poplar tree", audioUrl: "yang" },
    { surname: "黄", pinyin: "Huáng", meaning: "Yellow", audioUrl: "huang" },
    { surname: "赵", pinyin: "Zhào", meaning: "State of Zhao", audioUrl: "zhao" },
    { surname: "周", pinyin: "Zhōu", meaning: "Zhou Dynasty", audioUrl: "zhou" },
    { surname: "吴", pinyin: "Wú", meaning: "State of Wu", audioUrl: "wu" }
  ];

  // Stop any currently playing audio
  const stopAudio = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    if (utteranceRef.current) {
      utteranceRef.current = null;
    }
    setPlaying(null);
  };

  // Play pinyin pronunciation using Web Speech API
  const playAudio = (pinyin: string, character: string, audioUrl: string, isFullName: boolean = false) => {
    // Stop any currently playing audio
    if (playing) {
      stopAudio();
      // If clicking the same button, just stop
      if (playing === audioUrl) {
        return;
      }
    }

    // Check if browser supports speech synthesis
    if (!window.speechSynthesis) {
      alert('Your browser does not support text-to-speech. Please try a modern browser like Chrome, Edge, or Safari.');
      return;
    }

    // Create utterance - use character(s) for pronunciation
    const textToSpeak = isFullName ? character : character;
    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    
    // Set language to Chinese (Mandarin)
    utterance.lang = 'zh-CN';
    utterance.rate = 0.8; // Slightly slower for clarity
    utterance.pitch = 1;
    utterance.volume = 1;

    // Try to find a Chinese voice
    const voices = window.speechSynthesis.getVoices();
    const chineseVoice = voices.find(voice => 
      voice.lang.startsWith('zh') || 
      voice.name.toLowerCase().includes('chinese') ||
      voice.name.toLowerCase().includes('mandarin')
    );
    
    if (chineseVoice) {
      utterance.voice = chineseVoice;
    }

    // Handle end of speech
    utterance.onend = () => {
      setPlaying(null);
      utteranceRef.current = null;
    };

    // Handle errors
    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      setPlaying(null);
      utteranceRef.current = null;
    };

    utteranceRef.current = utterance;
    setPlaying(audioUrl);
    
    // Play the speech
    window.speechSynthesis.speak(utterance);
  };

  // Load voices when component mounts
  useEffect(() => {
    const loadVoices = () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.getVoices();
      }
    };

    loadVoices();
    // Some browsers load voices asynchronously
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    // Cleanup: stop any playing audio when component unmounts
    return () => {
      stopAudio();
    };
  }, []);

  return (
    <>
      <SeoMeta
        title="Chinese Name Pronunciation Guide - Master Tones & Sounds"
        description="Struggling to pronounce Chinese names correctly? Master the 4 tones and neutral tone with our comprehensive pronunciation guide. Learn how to pronounce Chinese names with audio examples, tone marks, and practice tips. Avoid embarrassing mispronunciations and speak Chinese names with confidence. Essential for anyone using a Chinese name."
        keywords="chinese name pronunciation, how to pronounce chinese names, chinese pronunciation guide, chinese tones pronunciation, chinese name pronunciation audio, learn chinese pronunciation, chinese name tones, pinyin pronunciation guide"
        pathname="/pronunciation"
      />
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-gray-900">Pronunciation Guide</h1>
            <p className="mt-4 text-lg text-gray-500">
              Master the tones and sounds of Chinese names
            </p>
          </div>

        {/* Introduction */}
        <div className="mt-12 max-w-3xl mx-auto">
          <div className="bg-indigo-50 rounded-lg p-6">
            <div className="flex items-center">
              <Volume2 className="h-8 w-8 text-indigo-600 mr-4" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Understanding Chinese Tones</h2>
                <p className="mt-2 text-gray-600">
                  Chinese is a tonal language where the same syllable pronounced with different tones can have different meanings. 
                  Mastering these tones is crucial for proper pronunciation of Chinese names.
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  💡 Click the play button next to each character to hear its pronunciation. Make sure your device volume is turned on.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tone Guides */}
        <div className="mt-12">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {toneGuides.map((guide, index) => (
              <div
                key={index}
                className="bg-white overflow-hidden shadow-lg rounded-lg hover:shadow-xl transition-shadow"
              >
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {guide.tone}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {guide.description}
                  </p>
                  <div className="space-y-4">
                    {guide.examples.map((example, exIndex) => (
                      <div key={exIndex} className="flex items-center justify-between">
                        <div>
                          <span className="text-2xl font-bold text-gray-900 mr-2">
                            {example.character}
                          </span>
                          <span className="text-indigo-600">{example.pinyin}</span>
                          <p className="text-sm text-gray-500">{example.meaning}</p>
                        </div>
                        <button
                          onClick={() => playAudio(example.pinyin, example.character, example.audioUrl)}
                          className="p-2 rounded-full bg-indigo-100 hover:bg-indigo-200 transition-colors"
                          title={`Pronounce: ${example.pinyin} (${example.character})`}
                        >
                          {playing === example.audioUrl ? (
                            <Pause className="h-5 w-5 text-indigo-600" />
                          ) : (
                            <Play className="h-5 w-5 text-indigo-600" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Common Surnames Section */}
        <section className="mt-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-extrabold text-gray-900">Common Chinese Surnames Pronunciation</h2>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
              Practice pronouncing the most common Chinese surnames. Click the play button to hear each surname pronounced correctly.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {commonSurnames.map((surname, index) => (
              <div
                key={index}
                className="bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-shadow text-center"
              >
                <div className="text-4xl font-bold text-gray-900 mb-2">{surname.surname}</div>
                <div className="text-indigo-600 font-medium mb-1">{surname.pinyin}</div>
                <div className="text-sm text-gray-500 mb-3">{surname.meaning}</div>
                <button
                  onClick={() => playAudio(surname.pinyin, surname.surname, surname.audioUrl)}
                  className="w-full p-2 rounded-full bg-indigo-100 hover:bg-indigo-200 transition-colors flex items-center justify-center gap-2"
                  title={`Pronounce: ${surname.pinyin}`}
                >
                  {playing === surname.audioUrl ? (
                    <>
                      <Pause className="h-4 w-4 text-indigo-600" />
                      <span className="text-xs text-indigo-600">Stop</span>
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 text-indigo-600" />
                      <span className="text-xs text-indigo-600">Play</span>
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Common Names Section */}
        <section className="mt-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-extrabold text-gray-900">Common Chinese Names Pronunciation</h2>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
              Practice pronouncing complete Chinese names. These examples combine surnames with given names to help you master full name pronunciation.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {commonNames.map((name, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-gray-900 mb-2">{name.fullName}</div>
                  <div className="text-indigo-600 font-medium mb-1">{name.pinyin}</div>
                  <div className="text-sm text-gray-600">{name.meaning}</div>
                </div>
                <button
                  onClick={() => playAudio(name.pinyin, name.fullName, name.audioUrl, true)}
                  className="w-full p-3 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
                  title={`Pronounce: ${name.pinyin}`}
                >
                  {playing === name.audioUrl ? (
                    <>
                      <Pause className="h-5 w-5" />
                      <span>Stop</span>
                    </>
                  ) : (
                    <>
                      <Play className="h-5 w-5" />
                      <span>Play Name</span>
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Practice Tips */}
        <section className="mt-16">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">Practice Tips</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900">Listen Carefully</h3>
                <p className="mt-2 text-gray-600">
                  Pay attention to native speakers and try to mimic their tone patterns. Use the play buttons to hear each character and name multiple times.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900">Practice Daily</h3>
                <p className="mt-2 text-gray-600">
                  Spend a few minutes each day practicing the different tones. Start with individual characters, then move to full names.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900">Record Yourself</h3>
                <p className="mt-2 text-gray-600">
                  Compare your pronunciation with the audio examples. Focus on matching the tone patterns accurately.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Additional Resources */}
        <section className="mt-16 bg-gray-50 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related Resources</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <a href="/name-generator" className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Name Generator</h3>
              <p className="text-sm text-gray-600">Generate your Chinese name with pronunciation</p>
            </a>
            <a href="/name-meanings" className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Character Meanings</h3>
              <p className="text-sm text-gray-600">Learn the meanings of Chinese characters</p>
            </a>
            <a href="/chinese-boy-names" className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Boy Names</h3>
              <p className="text-sm text-gray-600">Explore Chinese boy names with pronunciations</p>
            </a>
          </div>
        </section>
      </div>
    </div>
    </>
  );
}